import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectsApi } from '../../api/client';
import { Project } from '../../types';
import { X, Printer, Loader2, AlertCircle, Package, Truck, MapPin, Tag } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShippingLabelModalProps {
  project: Project;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function carrierLabel(carrier: string | null): string {
  if (!carrier) return '—';
  const map: Record<string, string> = {
    correos: 'Correos',
    mrw: 'MRW',
    seur: 'SEUR',
    gls: 'GLS',
    dhl: 'DHL',
    ups: 'UPS',
    nacex: 'Nacex',
    otro: 'Otro',
  };
  return map[carrier] ?? carrier.toUpperCase();
}

function boxTypeLabel(boxType: string | null): string {
  if (!boxType) return '—';
  const map: Record<string, string> = {
    caja_pequena: 'Caja pequeña',
    caja_mediana: 'Caja mediana',
    caja_grande: 'Caja grande',
    sobre: 'Sobre',
    palet: 'Palet',
    otro: 'Otro',
  };
  return map[boxType] ?? boxType;
}

// ─── Label Section ────────────────────────────────────────────────────────────

function LabelSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid #000', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{
        background: '#000', color: '#fff', padding: '4px 10px',
        fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
        fontFamily: 'JetBrains Mono, monospace',
      }}>
        {title}
      </div>
      <div style={{ padding: '10px 12px' }}>
        {children}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ShippingLabelModal({ project, onClose }: ShippingLabelModalProps) {
  const embalajeStep = project.steps.find(s => s.department === 'embalaje');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['shipping-label', embalajeStep?.id],
    queryFn: () => {
      if (!embalajeStep) throw new Error('Paso de embalaje no encontrado');
      return projectsApi.getShippingLabel(embalajeStep.id);
    },
    enabled: !!embalajeStep,
  });

  const label = data?.data;

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.80)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#12121a',
          border: '1px solid #1e1e2e',
          borderRadius: 20,
          width: '100%',
          maxWidth: 560,
          boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(249,115,22,0.08)',
          overflow: 'hidden',
          animation: 'modalIn 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Header accent */}
        <div style={{ height: 3, background: 'linear-gradient(90deg, #f97316, #ea580c)' }} />

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px 16px',
          borderBottom: '1px solid #1a1a28',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Tag size={20} color="#f97316" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#e8e6f4', fontFamily: 'Syne, sans-serif', letterSpacing: '-0.02em' }}>
                Etiqueta de Envío
              </div>
              <div style={{ fontSize: 11, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
                {project.reference} — {project.title}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {label && (
              <button
                onClick={() => window.print()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  border: 'none', borderRadius: 8,
                  padding: '8px 14px',
                  color: '#fff',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: '0 3px 10px rgba(249,115,22,0.35)',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                <Printer size={13} />
                Imprimir
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#4a4858', padding: 6, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#e2e0f0')}
              onMouseLeave={e => (e.currentTarget.style.color = '#4a4858')}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '32px 0', color: '#6b6880' }}>
              <Loader2 size={20} style={{ animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Cargando datos...</span>
            </div>
          )}

          {isError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 8, padding: '12px 16px',
              fontSize: 13, color: '#f87171',
            }}>
              <AlertCircle size={15} />
              Error al cargar los datos de la etiqueta.
            </div>
          )}

          {!isLoading && !isError && label && (
            <>
              {/* Printable label */}
              <div
                id="shipping-label-print"
                style={{
                  background: '#fff',
                  border: '2px solid #000',
                  borderRadius: 8,
                  padding: 16,
                  fontFamily: 'Arial, sans-serif',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {/* Top row: sender + reference */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'start' }}>
                  {/* Sender */}
                  <LabelSection title="REMITENTE">
                    <div style={{ fontSize: 12, color: '#000', lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 700 }}>ERP Imprenta</div>
                      <div>Departamento de Embalaje</div>
                    </div>
                  </LabelSection>

                  {/* Reference */}
                  <LabelSection title="REFERENCIA">
                    <div style={{ fontSize: 18, fontWeight: 900, color: '#000', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>
                      {label.project?.reference ?? project.reference}
                    </div>
                  </LabelSection>
                </div>

                {/* Recipient */}
                <LabelSection title="DESTINATARIO">
                  <div style={{ fontSize: 13, color: '#000', lineHeight: 1.8 }}>
                    {label.client ? (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 800 }}>
                          {label.client.name}
                        </div>
                        {label.client.company && (
                          <div style={{ fontWeight: 600 }}>{label.client.company}</div>
                        )}
                        {label.client.address && <div>{label.client.address}</div>}
                        <div style={{ fontWeight: 700 }}>
                          {[label.client.postalCode, label.client.city].filter(Boolean).join(' ')}
                          {label.client.country ? ` — ${label.client.country}` : ''}
                        </div>
                        {label.client.phone && (
                          <div style={{ color: '#555', fontSize: 11 }}>Tel: {label.client.phone}</div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: '#888', fontStyle: 'italic' }}>Sin datos de cliente</div>
                    )}
                  </div>
                </LabelSection>

                {/* Packaging info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <LabelSection title="EMBALAJE">
                    <div style={{ fontSize: 12, color: '#000', lineHeight: 1.7 }}>
                      <div><b>Tipo:</b> {boxTypeLabel(label.packaging?.boxType)}</div>
                      <div><b>Bultos:</b> {label.packaging?.bundleCount ?? '—'}</div>
                      <div><b>Peso est.:</b> {label.packaging?.estimatedWeight != null ? `${label.packaging.estimatedWeight} kg` : '—'}</div>
                      {label.packaging?.notes && (
                        <div style={{ fontSize: 10, color: '#555', marginTop: 4 }}>{label.packaging.notes}</div>
                      )}
                    </div>
                  </LabelSection>

                  <LabelSection title="ENVÍO">
                    <div style={{ fontSize: 12, color: '#000', lineHeight: 1.7 }}>
                      <div><b>Transportista:</b> {carrierLabel(label.shipping?.carrier)}</div>
                      {label.shipping?.shippedAt && (
                        <div><b>Enviado:</b> {new Date(label.shipping.shippedAt).toLocaleDateString('es-ES')}</div>
                      )}
                    </div>
                  </LabelSection>
                </div>

                {/* Tracking */}
                {label.shipping?.trackingNumber && (
                  <LabelSection title="NUMERO DE SEGUIMIENTO">
                    <div style={{ textAlign: 'center', padding: '4px 0' }}>
                      {/* Simulated barcode — thick letter spacing to suggest bars */}
                      <div style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 28,
                        fontWeight: 900,
                        letterSpacing: '0.25em',
                        color: '#000',
                        borderBottom: '3px solid #000',
                        paddingBottom: 4,
                        marginBottom: 4,
                      }}>
                        {label.shipping.trackingNumber}
                      </div>
                      {/* Simulated barcode graphic */}
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 1, marginBottom: 4 }}>
                        {label.shipping.trackingNumber.split('').map((char: string, i: number) => {
                          const w = (char.charCodeAt(0) % 3) + 1;
                          return (
                            <div
                              key={i}
                              style={{
                                width: w * 2,
                                height: 32,
                                background: '#000',
                                flexShrink: 0,
                              }}
                            />
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 11, color: '#000', letterSpacing: '0.08em', fontFamily: 'JetBrains Mono, monospace' }}>
                        {label.shipping.trackingNumber}
                      </div>
                    </div>
                  </LabelSection>
                )}

                {/* Project title */}
                <div style={{ borderTop: '1px solid #ddd', paddingTop: 8, fontSize: 11, color: '#666', fontStyle: 'italic', textAlign: 'center' }}>
                  {label.project?.title ?? project.title}
                </div>
              </div>

              {/* Hint */}
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)', borderRadius: 8 }}>
                <Printer size={12} color="#f97316" />
                <span style={{ fontSize: 11, color: '#8b89a0', fontFamily: 'JetBrains Mono, monospace' }}>
                  Usa el botón Imprimir para obtener una copia en papel.
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media print {
          body > *:not(#shipping-label-print) { display: none !important; }
          #shipping-label-print {
            position: fixed !important;
            top: 0 !important; left: 0 !important;
            width: 100% !important;
            box-shadow: none !important;
            border: 2px solid #000 !important;
          }
        }
      `}</style>
    </div>
  );
}
